import React, { useEffect, useRef, useState, useMemo } from "react";
import Theme from "@amzn/meridian/theme";
import Box from "@amzn/meridian/box";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Heading from "@amzn/meridian/heading";
import Text from "@amzn/meridian/text";
import Button from "@amzn/meridian/button";
import Tag from "@amzn/meridian/tag";
import Select, { SelectOption } from "@amzn/meridian/select";
import Tab, { TabGroup } from "@amzn/meridian/tab";
import { colorBlue500 } from "@amzn/meridian-tokens/base/color";
import MetricsSection from "../../../../components/MetricsSection/v2/MetricsSection";
import RosterAndRates from "../../../rosterAndRates";
import VolumesAndWipContainer from "../../../volumesAndWip/VolumesAndWipContainer";
import ShiftDataTable from "../../../../components/ShiftDataTable/v2/ShiftDataTableContainer";
import ShiftTotalsSection from "../../../../components/ShiftTotalsSection/v2/ShiftTotalsSectionContainer";
import Chart from "../../../reviewAndPublish/components/v2/Chart";
import PlanActionsCard from "./PlanActionsCard";
import CheckoutOverridesView from "./CheckoutOverridesView";
import { SPMSGetShiftPlanResponse, PlanVersion } from "../../../../types/shiftPlan";
import { getYesterdayDate } from "../../models";
import { PLAN_VIEW_OPTIONS, DEFAULT_PLAN_VIEW, RCC_PROPOSED_PLAN_VIEW, OPERATOR_OVERRIDE_PLAN_VIEW } from "./planViewOptions";
import { findBaselinePlan, getOverridePlans, isBaselinePlan } from "./baselinePlan";
import { getPlanStatusTag } from "./planStatusTag";
import { getShiftPlanOutput as fetchShiftPlanOutput } from "../../../reviewAndPublish/api";
import { buildGetShiftPlanOutputRequest, GetShiftPlanOutputRequest, ProjectedVolumeDataPoint, GetShiftPlanOutputResponse } from "../../../reviewAndPublish/model";
import { createShiftPlan, getClockWindowNameBasedOnSelectedOption, getPlanTypeBasedOnSelectedTab } from "../../../../services/shiftPlanService";
import { useAppDispatch } from "../../../../store";
import { showNotification } from "../../../../store/slices/errorSlice";
import { publishShiftPlanThunk } from "../../../../store/slices/reviewAndPublishSlice";
import { PostPublishView } from "../../../../store/slices/v2/publishedShiftPlanFeatureSlice";
import { ErrorCode, PlanStatus, REQUEST_SOURCE_PUBLISHED_PLAN } from "../../../../utils/constants";
import { convertUTCToLocalTime } from "../../../../utils/string-utils";
import { useResolveMidShiftReplan } from "../../../../hooks/useResolveMidShiftReplan";
import {
    applyActualsGate,
    getVolumeTrend,
    splitVolumeSeries,
    withoutActualPoints,
} from "../../../../utils/midShiftActuals";

/** Value of the Start of Shift tab (see SHIFT_PLAN_TABS in pages/shiftPlanning/index.tsx). */
const SOS_TAB = "sos";

/**
 * Refactored (V2) published shift plan page — UTR-REFLEX-1163.
 *
 * Built incrementally behind the RCC_PUBLISHED_SHIFT_PLAN_V2 gate. So far this
 * page renders the header (Plan View + Version selectors, "Viewing" badge,
 * Start-New-Shift-Plan button — UTR-REFLEX-1166/1167), the three metric cards
 * (UTR-REFLEX-1168), and the Process Path table (UTR-REFLEX-1165). The chart and
 * shift totals land in follow-up CRs. Kept as a sibling of the V1 page
 * (CurrentPlanSummaryContainer) so V1 is never touched. Layout and surfaces come
 * from Meridian (Theme/Box/Row/Column/Tag/Select), so there's no custom CSS.
 */
interface CurrentPlanSummaryProps {
    shiftPlans: SPMSGetShiftPlanResponse[];
    selectedTab: string;
    selectedDate: string;
    selectedShift: string;
    /** Called when plan creation reports an existing plan — parent opens the modal. */
    onNeedsTakeOver: (displayMessage: string) => void;
    /** Called on successful plan creation — parent handles dispatch + navigate. */
    onPlanSuccess: (planId: string) => void;
}

interface CurrentPlanSummaryStateProps {
    planId: string | null;
    stationCode: string | null;
    /** One-shot pointer (set by the publish flow) to the just-published plan to
     *  open on — selects it and the Operator Override Plan view. */
    postPublishView?: PostPublishView | null;
    shiftPlanOutput?: {
        planId?: string;
        planStatus?: string;
        overrides?: { total?: number };
        totalOverrides?: number;
        shiftSummary?: unknown;
        /** @deprecated Read the volume curve through getVolumeTrend. */
        projectedPrimaryGradingVolume?: ProjectedVolumeDataPoint[];
        primaryGradingVolumeTrend?: ProjectedVolumeDataPoint[];
    };
}

interface CurrentPlanSummaryDispatchProps {
    getShiftPlanOutput: (getShiftPlanOutputRequest: GetShiftPlanOutputRequest) => void;
    showError: (errorPayload: { title: string; message: string }) => void;
}

type Props = CurrentPlanSummaryProps & CurrentPlanSummaryStateProps & CurrentPlanSummaryDispatchProps;

const CurrentPlanSummary: React.FC<Props> = ({
    shiftPlans,
    selectedTab,
    selectedDate,
    selectedShift,
    stationCode,
    postPublishView,
    shiftPlanOutput,
    getShiftPlanOutput,
    showError,
    onNeedsTakeOver,
    onPlanSuccess,
}) => {
    const dispatch = useAppDispatch();
    // When we arrived here right after publishing a plan (UTR-REFLEX-1163), open on
    // that exact plan and the Operator Override Plan view — the operator should land
    // on the plan they just generated, not the RCC default. Otherwise default to RCC.
    const publishedPlan = postPublishView
        ? shiftPlans.find((plan) => plan.planId === postPublishView.planId)
        : undefined;
    const [selectedPlan, setSelectedPlan] = useState(() => publishedPlan ?? shiftPlans[0]);
    const [selectedPlanView, setSelectedPlanView] = useState<string>(
        postPublishView ? OPERATOR_OVERRIDE_PLAN_VIEW : DEFAULT_PLAN_VIEW
    );
    const [isGenerating, setIsGenerating] = useState<boolean>(false);
    const [isPublishing, setIsPublishing] = useState<boolean>(false);
    // The in-flight guard has to be a ref, not `isPublishing`: two clicks landing in
    // the same React batch both read the pre-update state, so a state-based check
    // would let the plan be published twice.
    const publishInFlightRef = useRef<boolean>(false);
    // Plans approved from this page during this session. The plan list belongs to the
    // parent and isn't refetched on publish, so its status stays PENDING_APPROVAL —
    // remembering the ids here is what stops the Publish button coming back.
    const [approvedPlanIds, setApprovedPlanIds] = useState<string[]>([]);
    // Sub-view tabs mirroring the Review & Publish page: the plan's shift-plan
    // summary plus read-only Rates & Roster and Volume & Buffer views. The two
    // read-only views reuse the workflow components, fetching by the selected
    // version's planId (UTR-REFLEX-1163).
    const [activeTab, setActiveTab] = useState<string>("shift-plan");
    // The "other" plan's projected curve, overlaid on the chart when both plans
    // exist. Fetched via getShiftPlanOutput (not the shared thunk — that single
    // slot feeds the primary/selected plan) and kept in local state.
    const [overlaySeries, setOverlaySeries] = useState<ProjectedVolumeDataPoint[] | null>(null);
    // Mid-shift actual-vs-projected display (RCC_MID_SHIFT_REPLAN, user OR site), resolved into Redux
    // so the chart and the Process Path table read the same flag. Fail closed until resolved.
    const showActuals = useResolveMidShiftReplan(stationCode);

    // Short label of the currently selected Plan View, shown in the "Viewing" badge
    // (without the dropdown's "(Latest Published)" suffix). selectedPlanView is always
    // one of PLAN_VIEW_OPTIONS (init from DEFAULT_PLAN_VIEW, only changed via the
    // dropdown), so the lookup always matches.
    const selectedPlanViewLabel =
        PLAN_VIEW_OPTIONS.find((option) => option.value === selectedPlanView)!.badgeLabel;

    const isRccProposedView = selectedPlanView === RCC_PROPOSED_PLAN_VIEW;

    // The auto-generated baseline plan (RCC Proposed), if one exists. Identified via
    // the dedicated isBaselinePlan helper (single swap point for the future
    // auto-generation implementation).
    const baselinePlan = useMemo(() => findBaselinePlan(shiftPlans), [shiftPlans]);

    // Versions shown in the Version dropdown depend on the Plan View:
    //  - RCC Proposed Plan: only the single auto-generated baseline plan (if any).
    //  - Operator Override Plan: all non-baseline (operator-created) plans.
    // NOTE: derived client-side from the mock; the backend returns the correct
    // per-view version set in UTR-REFLEX-1172.
    const visibleVersions = useMemo(() => {
        if (isRccProposedView) {
            return baselinePlan ? [baselinePlan] : [];
        }
        return getOverridePlans(shiftPlans);
    }, [isRccProposedView, shiftPlans, baselinePlan]);

    // Mid-shift re-plan (RCC_MID_SHIFT_REPLAN): on SOS, once an operator has already created a plan
    // for this shift, generating another is a re-run, so the button says so. Counts operator plans by
    // anyone (the baseline RCC plan is excluded) and ignores the Plan View, so the label doesn't change
    // when switching views. Label only: the click still starts the same flow.
    const isRerun = showActuals
        && selectedTab === SOS_TAB
        && getOverridePlans(shiftPlans).length >= 1;
    const generatePlanLabel = isRerun ? "Rerun Shift Plan" : "Start New Shift Plan";

    // The most recently updated plan among the visible versions — the "(Latest)"
    // marker in the Version dropdown (per view, since each view lists a different set).
    const latestPlanId = useMemo(() => {
        if (visibleVersions.length === 0) {
            return undefined;
        }
        return visibleVersions.reduce((latest, plan) => {
            const latestTime = new Date(latest.updatedAt as string).getTime();
            const planTime = new Date(plan.updatedAt as string).getTime();
            return planTime > latestTime ? plan : latest;
        }).planId;
    }, [visibleVersions]);

    // Clean empty state whenever the selected view has no plans (RCC Proposed with
    // no baseline yet, or Operator Override with no operator plans).
    const showEmptyState = visibleVersions.length === 0;
    const emptyStateTitle = isRccProposedView
        ? 'No RCC Proposed Plan available'
        : 'No Operator Override Plan available';
    const emptyStateMessage = isRccProposedView
        ? "An auto-generated RCC Proposed Plan hasn't been published for this shift yet. Once it's generated, its metrics and process path breakdown will appear here."
        : "No Operator Override Plan has been created for this shift yet. Once an operator overrides the RCC Proposed Plan, it will appear here.";

    // Keep the selected version valid when the Plan View (and thus the version list)
    // changes — fall back to the first visible version.
    useEffect(() => {
        if (visibleVersions.length === 0) {
            return;
        }
        const stillVisible = visibleVersions.some((plan) => plan.planId === selectedPlan?.planId);
        if (!stillVisible) {
            setSelectedPlan(visibleVersions[0]);
        }
    }, [visibleVersions, selectedPlan]);

    // Refetch when the selected version OR the plan view changes — each view returns
    // its own metrics, totals, and series. Only fetch for a plan that's actually in
    // the current view's version list, so switching to an empty view doesn't fetch
    // with a stale plan id from the previous view.
    const selectedPlanInView = visibleVersions.some((plan) => plan.planId === selectedPlan?.planId);
    useEffect(() => {
        if (selectedPlanInView && selectedPlan?.planId && selectedDate && stationCode && selectedShift) {
            getShiftPlanOutput(
                buildGetShiftPlanOutputRequest(
                    selectedPlan.planId as string, selectedDate, stationCode, selectedShift, selectedPlanView
                )
            );
        }
    }, [selectedPlanInView, selectedPlan?.planId, selectedPlanView, selectedDate, stationCode, selectedShift, getShiftPlanOutput]);

    // The "other" plan whose curve overlays the chart when both plans exist:
    //  - RCC view    → overlay the (latest) operator override plan, if any.
    //  - Override view → overlay the RCC baseline plan, if any.
    // Overlay plan (the second line on the chart), per product:
    //  - RCC Proposed view: NO overlay — the RCC plan shows only its black line.
    //  - Operator Override view: overlay the RCC baseline, so the override plan
    //    shows both its orange line and the black RCC line for comparison.
    const overlayPlan = useMemo(
        () => (isRccProposedView ? undefined : baselinePlan),
        [isRccProposedView, baselinePlan]
    );

    // Fetch the overlay plan's curve directly (bypassing the shared thunk, which
    // feeds only the primary/selected plan) and hold it in local state. Cleared
    // when no overlay plan exists so a stale curve doesn't linger across views.
    useEffect(() => {
        let cancelled = false;
        if (overlayPlan?.planId && selectedDate && stationCode && selectedShift) {
            fetchShiftPlanOutput(
                buildGetShiftPlanOutputRequest(
                    overlayPlan.planId as string, selectedDate, stationCode, selectedShift, selectedPlanView
                )
            )
                .then((output) => { if (!cancelled) setOverlaySeries(getVolumeTrend(output) ?? null); })
                .catch(() => { if (!cancelled) setOverlaySeries(null); });
        } else {
            setOverlaySeries(null);
        }
        return () => { cancelled = true; };
    }, [overlayPlan?.planId, selectedPlanView, selectedDate, stationCode, selectedShift]);

    // Statuses this read-only page can render. CLOSED is included: a plan for a shift
    // that has already run is closed, and this page is how operators look back at it —
    // its inputs and outputs are the record of what the shift was planned with. Only a
    // plan that never produced output (FAILED, REJECTED, CANCELLED, still being
    // created) is refused.
    useEffect(() => {
        if (shiftPlanOutput && shiftPlanOutput.planStatus) {
            const status = shiftPlanOutput.planStatus;
            const viewableStatuses: string[] = [
                PlanStatus.PENDING_APPROVAL,
                PlanStatus.APPROVED,
                PlanStatus.CLOSED,
            ];
            if (!viewableStatuses.includes(status)) {
                showError({
                    title: "Invalid Plan Status",
                    message: "This plan is not valid. Please select any other valid plan",
                });
            }
        }
    }, [shiftPlanOutput, showError]);

    const handleSelectChange = (value: string) => {
        setSelectedPlan(shiftPlans.find((plan) => plan.planId === value) as SPMSGetShiftPlanResponse);
    };

    const onGeneratePlanClick = async () => {
        if (new Date(selectedDate) < new Date(getYesterdayDate())) {
            dispatch(showNotification({
                title: "Invalid Date",
                message: "Cannot generate plan for past date. Please try generating for another date.",
                type: "warning",
            }));
            return;
        }
        setIsGenerating(true);
        try {
            const response = await createShiftPlan(selectedDate,
                getClockWindowNameBasedOnSelectedOption(selectedShift), getPlanTypeBasedOnSelectedTab(selectedTab), PlanVersion.STATIC_V1);
            if (response.errorDetails?.errorCode === ErrorCode.PLAN_OWNED_BY_ANOTHER_USER) {
                onNeedsTakeOver(response.errorDetails.errorMessage);
            } else if (response.status && response.status !== 'FAILED' && response.planId) {
                onPlanSuccess(response.planId);
            }
        } catch (error) {
            dispatch(showNotification({
                title: "Error",
                message: "Failed to create shift plan. Please try again.",
                type: "error",
            }));
        } finally {
            // Always clear the loading state so the button can't stick on "Generating...".
            setIsGenerating(false);
        }
    };

    // Include isGenerating so the button can't be re-clicked mid-request (which
    // would fire concurrent createShiftPlan calls).
    const disableGenerateButton = !selectedDate || !selectedPlan || !selectedShift || isGenerating;

    const selectedPlanId = selectedPlan?.planId as string | undefined;

    // An RCC auto-generated plan is created in PENDING_APPROVAL — nothing has accepted
    // it yet. If the operator is happy with it as it stands there's no reason to walk
    // the whole override workflow just to approve it, so Publish is offered right here.
    // Status comes from the plan list; the plan output is the fallback for list
    // payloads that omit it, and only when it's the output of this same plan.
    const selectedPlanStatus =
        (selectedPlan?.status as string | undefined) ??
        (shiftPlanOutput?.planId === selectedPlanId ? shiftPlanOutput?.planStatus : undefined);
    // Same status, rendered for the operator. Plans published from this page count as
    // approved even though the parent's plan list still says PENDING_APPROVAL.
    const selectedPlanStatusTag = getPlanStatusTag(
        selectedPlanStatus,
        !!selectedPlanId && approvedPlanIds.includes(selectedPlanId)
    );
    const canPublishSelectedPlan =
        !!selectedPlanId &&
        isBaselinePlan(selectedPlan) &&
        selectedPlanStatus === PlanStatus.PENDING_APPROVAL &&
        !approvedPlanIds.includes(selectedPlanId);

    const onPublishClick = async () => {
        if (!selectedPlanId || publishInFlightRef.current) {
            return;
        }
        publishInFlightRef.current = true;
        setIsPublishing(true);
        try {
            // The thunk raises its own error dialog when it *rejects*. A fulfilled
            // action that does not come back APPROVED still means the plan was not
            // published, so that case is reported here — otherwise the button just
            // stops spinning and the operator has no idea whether it worked.
            const result = await dispatch(publishShiftPlanThunk({
                planId: selectedPlanId,
                shiftType: getClockWindowNameBasedOnSelectedOption(selectedShift),
            }));
            const response = result.payload as { status?: string } | undefined;
            if (response?.status === PlanStatus.APPROVED) {
                setApprovedPlanIds((ids) => [...ids, selectedPlanId]);
                dispatch(showNotification({
                    title: "Published Successfully",
                    message: "RCC proposed plan published successfully!",
                    type: "success",
                }));
                // Reload the plan's output so the page reflects the approved plan
                // rather than the pending one the operator just accepted.
                if (selectedDate && stationCode && selectedShift) {
                    getShiftPlanOutput(
                        buildGetShiftPlanOutputRequest(
                            selectedPlanId, selectedDate, stationCode, selectedShift, selectedPlanView
                        )
                    );
                }
            } else if (result?.meta?.requestStatus === "fulfilled") {
                dispatch(showNotification({
                    title: "Publish Not Completed",
                    message: response?.status
                        ? `The plan came back as ${response.status} instead of ${PlanStatus.APPROVED}. It has not been published — please try again.`
                        : "The plan was not published. Please try again.",
                    type: "error",
                }));
            }
        } finally {
            publishInFlightRef.current = false;
            setIsPublishing(false);
        }
    };

    // Chart series: the primary (selected) plan's curve comes from the shared
    // shiftPlanOutput; the overlay plan's curve is the locally-fetched overlaySeries.
    // Which is RCC vs Override depends on the view — in the RCC view the primary IS
    // the RCC line and the overlay is the override; in the Override view it's flipped.
    //
    // Mid-shift re-plan (RCC_MID_SHIFT_REPLAN): the selected plan's elapsed hours (dataKind ACTUAL)
    // become the green "Actually processed" line and its projected line starts where that ends. The
    // overlay plan contributes only its projected points, so a single green line is drawn. Flag off
    // (or still resolving): actual points are dropped and the chart reads as today.
    const volumeTrend = getVolumeTrend(shiftPlanOutput);
    const { actual: primaryActual, projected: primaryProjected } = useMemo(
        () => showActuals
            ? splitVolumeSeries(volumeTrend)
            : { actual: [], projected: withoutActualPoints(volumeTrend) },
        [showActuals, volumeTrend]
    );
    const overlayProjected = useMemo(() => withoutActualPoints(overlaySeries), [overlaySeries]);
    const rccSeries = isRccProposedView ? primaryProjected : overlayProjected;
    const overrideSeries = isRccProposedView ? overlayProjected : primaryProjected;
    // Same gate applied to the export, so the CSV never shows hours the screen hides.
    const exportedShiftPlanOutput = useMemo(
        () => applyActualsGate(shiftPlanOutput as GetShiftPlanOutputResponse | undefined, showActuals),
        [shiftPlanOutput, showActuals]
    );

    // Local time of when the selected plan was last generated (empty if unknown) —
    // reused for the Plan Actions "last generated at" line and the PDF metadata.
    const selectedPlanUpdatedAt = selectedPlan?.updatedAt
        ? convertUTCToLocalTime(selectedPlan.updatedAt as string)
        : '';

    return (
        // Nested theme: the app-wide theme is purple, but the V2 mocks call for blue
        // primary surfaces. Uses Meridian's built-in "blue" palette, scoped here so
        // V1 and the rest of the app keep their theme. `mode` (light/dark) inherited.
        <Theme palette="blue">
            <Box spacingInset="300">
                <Column spacing="400">
                    <Row alignmentVertical="top" spacing="400" widths={["fit", "fit", "fit", "fill", "fit"]}>
                        <Select label="Plan View" value={selectedPlanView} onChange={setSelectedPlanView} width={355}>
                            {PLAN_VIEW_OPTIONS.map((option) => (
                                <SelectOption key={option.value} label={option.label} value={option.value} />
                            ))}
                        </Select>
                        <Select label="Version" value={selectedPlan?.planId as string} onChange={handleSelectChange} width={300}>
                            {visibleVersions.map((plan) => (
                                <SelectOption
                                    key={plan.planId as string}
                                    label={
                                        plan.planId === latestPlanId
                                            ? `${convertUTCToLocalTime(plan.updatedAt as string)} (Latest)`
                                            : convertUTCToLocalTime(plan.updatedAt as string)
                                    }
                                    value={plan.planId as string}
                                />
                            ))}
                        </Select>
                        <Box width={3} minHeight={85} backgroundColor={colorBlue500} aria-hidden="true" />
                        <Column spacing="100" alignmentVertical="top">
                            <Text type="h100">
                                Current plan input summary ({shiftPlanOutput?.totalOverrides ?? 0} overrides)
                            </Text>
                            <Text type="b100" color="secondary">
                                Plan id: {shiftPlanOutput?.planId ?? ''}
                            </Text>
                            {/* Row wrapper so the Tags hug their text instead of
                                stretching to the Column's full width. */}
                            <Row spacing="200">
                                <Tag type="warning">{`Viewing: ${selectedPlanViewLabel}`}</Tag>
                                {/* Where this plan stands. Absent for a status this
                                    frontend doesn't recognise, rather than showing a
                                    raw backend enum token. */}
                                {selectedPlanStatusTag && (
                                    <Tag type={selectedPlanStatusTag.type}>
                                        {`Status: ${selectedPlanStatusTag.label}`}
                                    </Tag>
                                )}
                            </Row>
                        </Column>

                        {/* Publish sits alongside Start New Shift Plan, and takes the
                            primary treatment when it's available — accepting the
                            pending RCC plan is the main action at that point. */}
                        <Row spacing="300">
                            {canPublishSelectedPlan && (
                                <Button type="primary" onClick={onPublishClick} disabled={isPublishing}>
                                    {isPublishing ? "Publishing..." : "Publish Plan"}
                                </Button>
                            )}
                            <Button
                                type={canPublishSelectedPlan ? "secondary" : "primary"}
                                onClick={onGeneratePlanClick}
                                disabled={disableGenerateButton}
                            >
                                {isGenerating ? "Generating..." : generatePlanLabel}
                            </Button>
                        </Row>
                    </Row>

                    {showEmptyState ? (
                        <Box type="outline" spacingInset="600">
                            <Column spacing="200" alignmentHorizontal="center">
                                <Heading type="h400" level={2}>{emptyStateTitle}</Heading>
                                <Text type="b300" color="secondary" alignment="center">{emptyStateMessage}</Text>
                            </Column>
                        </Box>
                    ) : (
                        <>
                            <TabGroup
                                fill="line"
                                value={activeTab}
                                onChange={setActiveTab}
                                aria-label="Published plan views"
                            >
                                <Tab value="shift-plan">Shift Plan</Tab>
                                <Tab value="rates-roster">Rates &amp; Roster</Tab>
                                <Tab value="volume-buffer">Volume &amp; Buffer</Tab>
                                <Tab value="checkout-overrides">Checkout Overrides</Tab>
                            </TabGroup>

                            {activeTab === "shift-plan" && (
                                <>
                                    <MetricsSection isRccProposedView={isRccProposedView} />

                                    <Row widths={["fill", 440]} spacing="400" alignmentVertical="stretch">
                                        <Box type="outline" spacingInset="400">
                                            <Column spacing="300">
                                                <Heading type="h500" level={2}>{isRccProposedView ? "RCC Recommended Volume" : "RCC Recommended Volume - Overridden"}</Heading>
                                                <Chart
                                                    rccSeries={rccSeries}
                                                    overrideSeries={overrideSeries}
                                                    actualSeries={primaryActual}
                                                />
                                            </Column>
                                        </Box>
                                        <Box type="outline" spacingInset="400">
                                            <ShiftTotalsSection />
                                        </Box>
                                    </Row>

                                    <ShiftDataTable />

                                    <PlanActionsCard
                                        shiftPlanOutput={exportedShiftPlanOutput}
                                        exportMetadata={{
                                            showActuals,
                                            planType: selectedPlanViewLabel,
                                            isRccProposedView,
                                            version: selectedPlanUpdatedAt,
                                            date: selectedDate,
                                            shift: selectedShift,
                                            stationCode: stationCode ?? '',
                                        }}
                                        lastGeneratedBy={selectedPlan?.lastUpdatedBy as string | undefined}
                                        lastGeneratedAt={selectedPlanUpdatedAt || undefined}
                                    />
                                </>
                            )}

                            {/* Read-only Rates & Roster for the selected version, mirroring
                                Review & Publish. Fetches by the selected plan's id. */}
                            {activeTab === "rates-roster" && (
                                <RosterAndRates
                                    readOnly={true}
                                    forceHideLoader={true}
                                    planId={selectedPlan?.planId as string | undefined}
                                    date={selectedDate}
                                    shift={selectedShift}
                                    stationCode={stationCode}
                                    source={REQUEST_SOURCE_PUBLISHED_PLAN}
                                />
                            )}

                            {/* Read-only Volume & Buffer for the selected version. */}
                            {activeTab === "volume-buffer" && (
                                <VolumesAndWipContainer
                                    readOnly={true}
                                    planId={selectedPlan?.planId as string | undefined}
                                    date={selectedDate}
                                    shiftType={selectedShift}
                                    stationCode={stationCode}
                                    source={REQUEST_SOURCE_PUBLISHED_PLAN}
                                />
                            )}

                            {/* Read-only Checkout overrides, read back from the plan's
                                persisted overrides so they show for published and past
                                plans too — not only during plan review. */}
                            {activeTab === "checkout-overrides" && (
                                <CheckoutOverridesView planId={selectedPlanId} />
                            )}
                        </>
                    )}
                </Column>
            </Box>
        </Theme>
    );
};

export default CurrentPlanSummary;
